import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAssetStore } from '../store/assetStore';
import { uploadFileToCdn } from '../store/uploadStore';
import { api, AssetPayload, VendorAttribute } from '../lib/api';
import RichTextEditor from '../components/ui/RichTextEditor';
import { toast } from 'sonner';

interface VersionRow {
  key: number;
  ue_version: string;
  version_name: string;
  changelog: string;
  storage_path?: string | null;
  download_url?: string | null;
}

interface LicenseRow {
  key: number;
  name: string;
  price: string;
  description: string;
}

const DEFAULT_LICENSES: LicenseRow[] = [
  { key: 1, name: 'Personal', price: '', description: '' },
  { key: 2, name: 'Professional', price: '', description: '' },
  { key: 3, name: 'Enterprise', price: '', description: '' },
];

const FALLBACK_CATEGORIES = [
  'Environments',
  'Characters',
  'Props',
  'Materials',
  'Textures',
  'Blueprints',
  'VFX',
  'Tools',
  'Audio',
  'Animations',
];

const EMPTY_VERSION: VersionRow = {
  key: 1,
  ue_version: '',
  version_name: '',
  changelog: '',
};

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

function fileName(path: string | null | undefined): string {
  if (!path) return '';
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
}

function AssetEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editingId = id ? Number(id) : null;
  const isEditing = editingId !== null && !Number.isNaN(editingId);

  const { getAssetById, createProduct, updateProduct } = useAssetStore();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<string[]>(FALLBACK_CATEGORIES);
  const [images, setImages] = useState<string[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [attributes, setAttributes] = useState<VendorAttribute[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([{ ...EMPTY_VERSION }]);
  const [licenses, setLicenses] = useState<LicenseRow[]>(DEFAULT_LICENSES.map((l) => ({ ...l })));
  const [uploadingVersionKey, setUploadingVersionKey] = useState<number | null>(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.categories
      .list()
      .then((list) => {
        if (Array.isArray(list) && list.length) setCategories(list);
      })
      .catch(() => {
        /* fall back to the static list */
      });
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    getAssetById(editingId)
      .then((asset) => {
        if (!asset || cancelled) return;
        setName(asset.name);
        setDescription(asset.description);
        setPrice(String(asset.price ?? ''));
        setCategory(asset.category);
        const loadedImages = asset.images?.length
          ? asset.images
          : asset.thumbnail_url
            ? [asset.thumbnail_url]
            : [];
        setImages(loadedImages);
        setAttributes(asset.attributes ?? []);
        const rows: VersionRow[] = (asset.versions ?? []).map((v) => ({
          key: Date.now() + v.id,
          ue_version: v.ue_version,
          version_name: v.version_name ?? '',
          changelog: v.changelog ?? '',
          storage_path: v.storage_path,
          download_url: v.download_url,
        }));
        setVersions(rows.length ? rows : [{ ...EMPTY_VERSION }]);
        const licenseRows: LicenseRow[] = (asset.licenses ?? []).map((l) => ({
          key: Date.now() + l.id,
          name: l.name,
          price: String(l.price ?? ''),
          description: l.description ?? '',
        }));
        setLicenses(licenseRows.length ? licenseRows : DEFAULT_LICENSES.map((l) => ({ ...l })));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Asset not found');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [editingId, isEditing, getAssetById]);

  const addAttribute = () => {
    setAttributes((prev) => [...prev, { id: 0, name: '', value: '' }]);
  };

  const updateAttribute = (index: number, field: 'name' | 'value', value: string) => {
    setAttributes((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  };

  const removeAttribute = (index: number) => {
    setAttributes((prev) => prev.filter((_, i) => i !== index));
  };

  const addVersion = () => {
    setVersions((prev) => [
      ...prev,
      { ...EMPTY_VERSION, key: Date.now() + Math.floor(Math.random() * 1000) },
    ]);
  };

  const updateVersion = (key: number, field: keyof VersionRow, value: unknown) => {
    setVersions((prev) => prev.map((v) => (v.key === key ? { ...v, [field]: value } : v)));
  };

  const removeVersion = (key: number) => {
    setVersions((prev) => prev.filter((v) => v.key !== key));
  };

  const addLicense = () => {
    setLicenses((prev) => [
      ...prev,
      { key: Date.now() + Math.floor(Math.random() * 1000), name: '', price: '', description: '' },
    ]);
  };

  const updateLicense = (key: number, field: keyof LicenseRow, value: string) => {
    setLicenses((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };

  const removeLicense = (key: number) => {
    setLicenses((prev) => prev.filter((l) => l.key !== key));
  };

  const handleImageUpload = async (files: FileList) => {
    setUploadingImage(true);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadFileToCdn(file);
        setImages((prev) => [...prev, result.url]);
      }
      toast.success('Image uploaded');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (url: string) => {
    setImages((prev) => prev.filter((u) => u !== url));
  };

  const handleVersionFileUpload = async (key: number, file: File) => {
    setUploadingVersionKey(key);
    try {
      const result = await uploadFileToCdn(file);
      setVersions((prev) =>
        prev.map((v) =>
          v.key === key
            ? {
                ...v,
                version_name: v.version_name || file.name,
                storage_path: result.storage_path,
                download_url: null,
              }
            : v
        )
      );
      toast.success('Asset package uploaded');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploadingVersionKey(null);
    }
  };

  const clearVersionFile = (key: number) => {
    setVersions((prev) =>
      prev.map((v) => (v.key === key ? { ...v, storage_path: null, download_url: null } : v))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!stripHtml(description)) {
      setError('Description is required');
      return;
    }
    const parsedPrice = parseFloat(price);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setError('Please enter a valid base price');
      return;
    }
    if (!category.trim()) {
      setError('Please select a category');
      return;
    }
    if (images.length === 0) {
      setError('Please upload at least one product image');
      return;
    }

    const cleanLicenses = licenses.filter((l) => l.name.trim());
    if (cleanLicenses.length === 0) {
      setError('Please define at least one license');
      return;
    }
    for (const lic of cleanLicenses) {
      const licPrice = parseFloat(lic.price);
      if (Number.isNaN(licPrice) || licPrice < 0) {
        setError(`Please enter a valid price for the "${lic.name}" license`);
        return;
      }
    }

    const activeVersions = versions.filter(
      (v) =>
        v.ue_version.trim() ||
        v.version_name?.trim() ||
        v.changelog?.trim() ||
        v.storage_path ||
        v.download_url
    );
    if (activeVersions.length === 0) {
      setError('Add at least one version with an Unreal Engine version and an asset file');
      return;
    }
    const seenUe = new Set<string>();
    for (const v of activeVersions) {
      const ue = v.ue_version.trim();
      if (!ue) {
        setError('Each version needs an Unreal Engine version (e.g. 5.6)');
        return;
      }
      const ueKey = ue.toLowerCase();
      if (seenUe.has(ueKey)) {
        setError(`Only one asset is allowed per Unreal Engine version (${ue})`);
        return;
      }
      seenUe.add(ueKey);
      if (!v.storage_path && !v.download_url) {
        setError(`Upload an asset package for Unreal Engine ${ue}`);
        return;
      }
    }

    const payload: AssetPayload = {
      name: name.trim(),
      description: description.trim(),
      price: parsedPrice,
      category: category.trim(),
      thumbnail_url: images[0] ?? null,
      images,
      attributes: attributes.filter((a) => a.name.trim() && a.value.trim()),
      versions: activeVersions.map((v) => ({
        ue_version: v.ue_version.trim(),
        version_name: v.version_name?.trim() || null,
        changelog: v.changelog?.trim() || null,
        storage_path: v.storage_path ?? null,
        download_url: v.download_url ?? null,
      })),
      licenses: cleanLicenses.map((l) => ({
        name: l.name.trim(),
        price: parseFloat(l.price),
        description: l.description.trim() || null,
      })),
    };

    setSaving(true);
    try {
      if (isEditing) {
        await updateProduct(editingId, payload);
        toast.success('Asset updated');
      } else {
        await createProduct(payload);
        toast.success('Asset created and submitted for review');
      }
      navigate('/assets');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save asset';
      setError(message);
      toast.error(message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1 className="page-title">{isEditing ? 'Edit Asset' : 'New Asset'}</h1>
      <p className="page-subtitle">
        {isEditing
          ? 'Update your asset details, versions, or package files.'
          : 'Create an asset. It will be reviewed by an admin before going live.'}
      </p>

      {error && <p className="form-error">{error}</p>}

      <form className="form" onSubmit={handleSubmit}>
        <div className="panel">
          <h2>Basic Information</h2>
          <div className="form-row">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Low Poly Dungeon Kit"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="description">Description *</label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe your asset…"
            />
          </div>
          <div className="form-row">
            <label htmlFor="price">Base Price (EUR) *</label>
            <input
              id="price"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">Select a category…</option>
              {category && !categories.includes(category) && (
                <option value={category}>{category}</option>
              )}
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="panel">
          <h2>Product Images</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Upload at least one image. The first image is used as the storefront thumbnail.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1rem',
            }}
          >
            {images.map((url) => (
              <div
                key={url}
                style={{
                  position: 'relative',
                  border: '1px solid var(--surface-border)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  aspectRatio: '4 / 3',
                  background: 'var(--surface)',
                }}
              >
                <img
                  src={url}
                  alt="Product"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => removeImage(url)}
                  style={{ position: 'absolute', top: 4, right: 4 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <label className="btn btn--sm btn--secondary" style={{ cursor: 'pointer' }}>
            {uploadingImage ? 'Uploading…' : '+ Add Images'}
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              disabled={uploadingImage}
              onChange={(e) => {
                if (e.target.files?.length) handleImageUpload(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        </div>

        <div className="panel">
          <h2>Licenses</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Offer one or more license tiers with their own prices. Customers choose one when buying.
          </p>
          <div className="dynamic-list">
            {licenses.map((lic) => (
              <div className="dynamic-row" key={lic.key}>
                <div className="row-fields">
                  <input
                    type="text"
                    placeholder="License name (e.g. Personal)"
                    value={lic.name}
                    onChange={(e) => updateLicense(lic.key, 'name', e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Price (EUR)"
                    value={lic.price}
                    onChange={(e) => updateLicense(lic.key, 'price', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={lic.description}
                    onChange={(e) => updateLicense(lic.key, 'description', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => removeLicense(lic.key)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <button type="button" className="btn btn--sm btn--secondary" onClick={addLicense}>
              + Add License
            </button>
          </div>
        </div>

        <div className="panel">
          <h2>Attributes</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Optional key-value facts about your asset (e.g. Polygon Count: 10k).
          </p>
          <div className="dynamic-list">
            {attributes.map((attr, index) => (
              <div className="dynamic-row" key={index}>
                <div className="row-fields">
                  <input
                    type="text"
                    placeholder="Name"
                    value={attr.name}
                    onChange={(e) => updateAttribute(index, 'name', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={attr.value}
                    onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => removeAttribute(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <button type="button" className="btn btn--sm btn--secondary" onClick={addAttribute}>
              + Add Attribute
            </button>
          </div>
        </div>

        <div className="panel">
          <h2>Versions &amp; Downloads</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Add one entry per Unreal Engine version and upload that version&apos;s asset package. A
            product can offer several UE versions, but only one asset per version.
          </p>

          <div className="dynamic-list">
            {versions.map((v) => {
              const hasFile = Boolean(v.storage_path || v.download_url);
              const uploading = uploadingVersionKey === v.key;
              return (
                <div
                  key={v.key}
                  style={{
                    border: '2px solid var(--surface-border)',
                    padding: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.6rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div className="row-fields">
                    <input
                      type="text"
                      placeholder="UE version (e.g. 5.6)"
                      value={v.ue_version}
                      onChange={(e) => updateVersion(v.key, 'ue_version', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Version name (e.g. v1.0)"
                      value={v.version_name ?? ''}
                      onChange={(e) => updateVersion(v.key, 'version_name', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <textarea
                      placeholder="Changelog"
                      value={v.changelog ?? ''}
                      onChange={(e) => updateVersion(v.key, 'changelog', e.target.value)}
                      style={{ minHeight: '60px' }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '0.75rem',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    {hasFile ? (
                      <>
                        <span style={{ fontSize: '0.9rem' }}>
                          Asset: <strong>{fileName(v.storage_path || v.download_url)}</strong>
                        </span>
                        <label
                          className="btn btn--sm btn--outline"
                          style={{ cursor: uploading ? 'wait' : 'pointer' }}
                        >
                          {uploading ? 'Uploading…' : 'Replace'}
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            accept=".zip,.rar,.7z,.upack,.uplugin"
                            disabled={uploading}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleVersionFileUpload(v.key, file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn btn--sm btn--danger"
                          onClick={() => clearVersionFile(v.key)}
                        >
                          Remove File
                        </button>
                      </>
                    ) : (
                      <label
                        className="btn btn--sm btn--outline"
                        style={{ cursor: uploading ? 'wait' : 'pointer' }}
                      >
                        {uploading ? 'Uploading…' : 'Upload Asset Package'}
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          accept=".zip,.rar,.7z,.upack,.uplugin"
                          disabled={uploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleVersionFileUpload(v.key, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>

                  {versions.length > 1 && (
                    <div>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={() => removeVersion(v.key)}
                      >
                        Remove Version
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn--sm btn--secondary" onClick={addVersion}>
              + Add Version
            </button>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn--primary btn--lg" disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Asset'}
          </button>
          <Link to="/assets" className="btn btn--secondary btn--lg">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

export default AssetEditor;
